import { useEffect, useState } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Stethoscope, Shield, Scan, HeartPulse, TestTubes, Microscope,
  Activity, Droplets, Baby, FlaskConical, Syringe, HeartHandshake,
  Waves, Gauge, Cross, Wind, Flower2, Brain, Apple, Eye, Ear,
  Zap, Bone, Dumbbell, Scissors, Thermometer, Pill, Ruler,
  AlertTriangle, Flame, Search, Plane, Scale, Heart, ClipboardList, Hand,
  Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  stethoscope: Stethoscope, shield: Shield, scan: Scan,
  "heart-pulse": HeartPulse, "test-tubes": TestTubes, microscope: Microscope,
  activity: Activity, droplets: Droplets, baby: Baby,
  "flask-conical": FlaskConical, syringe: Syringe, "heart-handshake": HeartHandshake,
  bandage: Cross, "iv-drip": Droplets, waves: Waves, gauge: Gauge,
  wind: Wind, flower: Flower2, brain: Brain, apple: Apple,
  eye: Eye, ear: Ear, zap: Zap, bone: Bone, dumbbell: Dumbbell,
  scissors: Scissors, thermometer: Thermometer, pill: Pill, ruler: Ruler,
  "alert-triangle": AlertTriangle, flame: Flame, search: Search,
  plane: Plane, scale: Scale, heart: Heart, "clipboard-list": ClipboardList, hand: Hand,
  sparkles: Sparkles,
};

const categoryColors = {
  "General Medicine": "bg-green-50 text-green-700",
  "Diagnostics": "bg-blue-50 text-blue-700",
  "Ultrasound": "bg-purple-50 text-purple-700",
  "Cardiac": "bg-red-50 text-red-700",
  "Wellness": "bg-orange-50 text-orange-700",
  "Emergency": "bg-rose-50 text-rose-700",
  "Respiratory": "bg-sky-50 text-sky-700",
  "Paediatric": "bg-teal-50 text-teal-700",
  "Skin Care": "bg-amber-50 text-amber-700",
  "ENT & Eye": "bg-indigo-50 text-indigo-700",
  "Pain & Rehab": "bg-yellow-50 text-yellow-700",
  "Mental Health": "bg-violet-50 text-violet-700",
  "Procedures": "bg-cyan-50 text-cyan-700",
  "Chemistry": "bg-red-50 text-red-700",
  "Microbiology": "bg-emerald-50 text-emerald-700",
  "Hormonal Panel (Women)": "bg-pink-50 text-pink-700",
  "Hormonal Panel (Men)": "bg-blue-50 text-blue-700",
  "Women's Health": "bg-pink-50 text-pink-700",
  "Sexual Health": "bg-rose-50 text-rose-700",
  "Aesthetics": "bg-fuchsia-50 text-fuchsia-700",
  "Surgery": "bg-slate-50 text-slate-700",
  "STI Testing": "bg-amber-50 text-amber-700",
};

export default function ServicesSection() {
  const [services, setServices] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data));
  }, []);

  const categories = ["All", ...new Set(services.map((s) => s.category))];
  const filtered = activeCategory === "All" ? services : services.filter((s) => s.category === activeCategory);
  const displayed = showAll || activeCategory !== "All" ? filtered : filtered.slice(0, 12);

  return (
    <section id="services" className="py-20 sm:py-28 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-green-100 text-green-800 border-0 mb-4 px-4 py-1.5 rounded-full text-sm font-medium">
            Our Services
          </Badge>
          <h2 className="font-[Outfit] text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight mb-4">
            Comprehensive Healthcare Services
          </h2>
          <p className="text-base md:text-lg text-stone-500 max-w-2xl mx-auto">
            From routine checkups to emergency care, diagnostics, cardiac imaging, surgery, and wellness — {services.length}+ services under one roof.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="service-category-filters">
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={activeCategory === cat ? "default" : "outline"}
              size="sm"
              onClick={() => { setActiveCategory(cat); setShowAll(false); }}
              data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full text-xs sm:text-sm px-4 sm:px-5 ${
                activeCategory === cat
                  ? "bg-green-700 hover:bg-green-800 text-white"
                  : "border-stone-200 text-stone-600 hover:border-green-600 hover:text-green-700"
              }`}
            >
              {cat}
              {cat !== "All" && (
                <span className="ml-1.5 text-[10px] opacity-70">
                  ({services.filter((s) => s.category === cat).length})
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {displayed.map((svc) => {
            const Icon = iconMap[svc.icon] || Stethoscope;
            const catColor = categoryColors[svc.category] || "bg-stone-50 text-stone-700";

            return (
              <div
                key={svc.id}
                data-testid={`service-card-${svc.id}`}
                className="group relative bg-white border border-stone-100 rounded-2xl p-6 transition-all duration-300 hover:border-green-600 hover:shadow-md"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-green-100 transition-colors">
                    <Icon className="w-5 h-5 text-green-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <h3 className="font-[Outfit] text-base font-semibold text-stone-900 leading-tight">
                        {svc.name}
                      </h3>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${catColor}`}>
                        {svc.category}
                      </span>
                    </div>
                    <p className="text-sm text-stone-500 leading-relaxed">
                      {svc.description}
                    </p>
                  </div>
                </div>
                <div className="absolute bottom-0 left-6 right-6 h-0.5 bg-green-700 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-full" />
              </div>
            );
          })}
        </div>

        {/* Show More/Less Button */}
        {activeCategory === "All" && filtered.length > 12 && (
          <div className="text-center mt-8">
            <Button
              variant="outline"
              onClick={() => setShowAll(!showAll)}
              data-testid="show-all-services-btn"
              className="rounded-full px-8 border-stone-200 text-stone-700 hover:border-green-600 hover:text-green-700"
            >
              {showAll ? `Show Less` : `View All ${filtered.length} Services`}
            </Button>
          </div>
        )}

        {/* Bottom Images Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          <div className="rounded-2xl overflow-hidden h-48 relative group">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/c9f675ad-548f-4c2a-9226-1cdbef424c9a/images/16350e321a8ad61693993f9abdc8ff859d7a4139811dee11790ca35f1d07d848.png"
              alt="Nigerian Female Sonographer performing ultrasound"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent flex items-end p-5">
              <div>
                <p className="text-white font-[Outfit] font-semibold">Ultrasound Imaging</p>
                <p className="text-stone-200 text-xs">5 scan types available</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden h-48 relative group">
            <img
              src="https://images.unsplash.com/photo-1679496124845-ac6957c8bffd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwbWVkaWNhbCUyMGxhYm9yYXRvcnklMjB1bHRyYXNvdW5kJTIwZGlhZ25vc3RpY3xlbnwwfHx8fDE3NzM1MDMzNTh8MA&ixlib=rb-4.1.0&q=85"
              alt="Laboratory Services"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent flex items-end p-5">
              <div>
                <p className="text-white font-[Outfit] font-semibold">Advanced Diagnostics</p>
                <p className="text-stone-200 text-xs">Complete lab facility</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden h-48 relative group">
            <img
              src="https://images.unsplash.com/photo-1704453961898-38a07a700128?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxuaWdlcmlhbiUyMGRvY3RvciUyMHN0ZXRob3Njb3BlJTIwc21pbGluZ3xlbnwwfHx8fDE3NzM1MDMzNTN8MA&ixlib=rb-4.1.0&q=85"
              alt="Nigerian Healthcare Team"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent flex items-end p-5">
              <div>
                <p className="text-white font-[Outfit] font-semibold">Cardiac Services</p>
                <p className="text-stone-200 text-xs">ECG & Echocardiography</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
